// client/src/components/shell/PageTitle.js
//
// The giant Instrument Serif page anchor at the top of every screen.
// Everything else on a screen orbits around this — it's what tells
// the user "you are on the Schedule tab" or "you are on Roster."
//
// Composes with the mischief primitives via children (put a Highlight
// or MarkerCircle inside the title text) and via the garnish slot
// (put a Scrawl/Arrow/Sticker beside the title).
//
// Usage:
//   <PageTitle>Schedule</PageTitle>
//
//   <PageTitle eyebrow="This season">Stats</PageTitle>
//
//   <PageTitle eyebrow="Your team">
//     <Highlight>Roster</Highlight>
//   </PageTitle>
//
//   <PageTitle
//     eyebrow="This week"
//     garnish={<Scrawl color="marker">← focus</Scrawl>}
//   >
//     Practice
//   </PageTitle>

export function PageTitle({
  children,
  eyebrow,
  garnish,
  className = '',
}) {
  return (
    // mb-6 gives breathing room before the screen's content starts.
    // Tunable if we want tighter/looser rhythm.
    <header className={`mb-6 ${className}`}>
      {/* Optional eyebrow. Rendered only if provided — no empty space
          reserved. Uses our eyebrow token: 10px uppercase, tracked wide,
          muted grey. Same style as section labels elsewhere. */}
      {eyebrow && (
        <p className="text-eyebrow uppercase text-inkSub mb-2">
          {eyebrow}
        </p>
      )}

      {/* Title + garnish row.
    - w-full + justify-between pushes the garnish to the right edge.
      This means the garnish's distance from the title depends on
      screen width, not on us guessing an offset against whatever
      mischief primitive is wrapping the title text. Wobbly ellipse
      today, curly underline tomorrow — spacing stays correct either
      way.
    - flex items-end so the garnish sits at the bottom of the title
      line, not floating up next to the ascenders.
    - flex-wrap so a long title + garnish don't overflow; garnish
      drops to a new line if needed. When it wraps, justify-between
      still works cleanly (garnish is on its own line, left-aligned
      by default in wrapped flex — which reads fine). */}
<div className="flex items-end justify-between flex-wrap w-full gap-3">
        {/* The title itself. Design tokens from HANDOFF §2:
            46px Instrument Serif, line-height 0.95, letter-spacing -1px.
            All bundled in the pageTitle fontSize token, so this is
            just font-serif + text-pageTitle + text-ink. */}
        <h1 className="font-serif text-pageTitle text-ink leading-[0.95]">
          {children}
        </h1>

        {/* The garnish slot. Rendered only if provided — no empty
            space reserved. Wrapped so it doesn't inherit h1 styling. */}
        {garnish && (
          <div className="pb-1">
            {garnish}
          </div>
        )}
      </div>
    </header>
  );
}